(() => {
  "use strict";

  const installationKey = "__masterKeyCustomerJourneyV1";
  const eventVersion = "1";
  const usageVisibilityRatio = 0.2;
  const usageVisibilityDelayMs = 800;
  const recommendationVisibilityRatio = 0.25;
  const recommendationVisibilityDelayMs = 600;
  const allowedEventNames = new Set([
    "masterkey:content_product_clicked",
    "masterkey:usage_section_viewed",
    "masterkey:faq_opened",
    "masterkey:faq_contact_clicked",
    "masterkey:contact_submitted",
    "masterkey:newsletter_subscribed",
    "masterkey:cart_recommendation_viewed",
    "masterkey:cart_recommendation_clicked",
  ]);

  if (window[installationKey]) return;
  window[installationKey] = true;

  let eventSequence = 0;
  let usageObserver = null;
  let usageObserverStarted = false;
  let recommendationObserver = null;
  let observedRecommendationElements = new WeakSet();
  let contactSuccessPublished = false;
  let newsletterSuccessPublished = false;
  const openedFaqItems = new WeakSet();
  const viewedUsageSections = new WeakSet();
  const viewedRecommendationKeys = new Set();
  const pendingUsageTimers = new Map();
  const pendingRecommendationTimers = new Map();

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  }

  function environment() {
    return window.Shopify?.theme?.role === "main" ? "production" : "staging";
  }

  function createEventId(name) {
    eventSequence += 1;
    const eventKind = name.split(":").pop()?.replace(/[^a-z0-9_]/gu, "") || "event";
    let randomToken = "";

    try {
      if (typeof window.crypto?.randomUUID === "function") {
        randomToken = window.crypto.randomUUID().replace(/-/gu, "");
      } else if (typeof window.crypto?.getRandomValues === "function") {
        const randomValues = new Uint32Array(2);
        window.crypto.getRandomValues(randomValues);
        randomToken = Array.from(randomValues, (value) => value.toString(36)).join("");
      }
    } catch {
      // A page-local sequence and timestamp remain as the final fallback.
    }

    return [
      "mkj",
      eventKind,
      Date.now().toString(36),
      eventSequence.toString(36),
      randomToken,
    ].filter(Boolean).join("_");
  }

  function publishEvent(name, payload) {
    if (!analyticsAllowed() || !allowedEventNames.has(name)) return false;

    const data = {
      journey_version: eventVersion,
      environment: environment(),
      event_id: createEventId(name),
      ...payload,
    };
    let attempted = false;
    const publisher = window.Shopify?.analytics?.publish;

    if (typeof publisher === "function") {
      attempted = true;
      try {
        const result = publisher.call(window.Shopify.analytics, name, data);
        if (result && typeof result.catch === "function") result.catch(() => {});
      } catch {
        // Tracking must never interrupt navigation or page interaction.
      }
    }

    if (Array.isArray(window.dataLayer)) {
      attempted = true;
      try {
        window.dataLayer.push({ event: name, ...data });
      } catch {
        // An existing third-party data layer is optional.
      }
    }

    if (attempted) {
      try {
        window.dispatchEvent(new CustomEvent("masterkey:journey-event", {
          detail: { name, data },
        }));
      } catch {
        // This event is an optional QA hook and is never a transport.
      }
    }

    return attempted;
  }

  function safeProductHandle(value) {
    if (typeof value !== "string") return "";
    const normalized = value.trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,127}$/u.test(normalized) ? normalized : "";
  }

  function safeContentHandle(value) {
    if (typeof value !== "string") return "";

    let decoded;
    try {
      decoded = decodeURIComponent(value).normalize("NFC");
    } catch {
      return "";
    }

    if (decoded.length < 1 || decoded.length > 160 || /[\u0000-\u001f\u007f/?#]/u.test(decoded)) return "";
    return decoded;
  }

  function currentPath() {
    try {
      return new URL(window.location.href).pathname.replace(/\/+$/u, "") || "/";
    } catch {
      return "/";
    }
  }

  function currentPageContext() {
    const path = currentPath();
    const segments = path.split("/").filter(Boolean);

    if (path === "/pages/guide") return { kind: "guide", handle: "guide" };
    if (path === "/pages/lifestyle-care-guide") return { kind: "guide", handle: "lifestyle-care-guide" };
    if (path === "/pages/care-support") return { kind: "care_support", handle: "care-support" };
    if (path === "/pages/faq") return { kind: "faq", handle: "faq" };
    if (path === "/pages/contact") return { kind: "contact", handle: "contact" };

    const productIndex = segments.lastIndexOf("products");
    if (productIndex >= 0 && segments[productIndex + 1]) {
      return {
        kind: "product",
        handle: safeProductHandle(document.body?.dataset?.mkProductHandle)
          || safeProductHandle(segments[productIndex + 1]),
      };
    }

    const journalIndex = segments.lastIndexOf("journal");
    if (journalIndex > 0 && segments[journalIndex - 1] === "blogs" && segments[journalIndex + 1]) {
      return { kind: "care_note", handle: safeContentHandle(segments[journalIndex + 1]) };
    }

    return { kind: "other", handle: "" };
  }

  function productHandleFromLink(anchor) {
    try {
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return "";
      const explicitHandle = safeProductHandle(anchor.dataset?.productHandle);
      if (explicitHandle) return explicitHandle;
      const segments = url.pathname.split("/").filter(Boolean);
      const productIndex = segments.lastIndexOf("products");
      return productIndex >= 0 ? safeProductHandle(segments[productIndex + 1]) : "";
    } catch {
      return "";
    }
  }

  function isContactLink(anchor) {
    try {
      const url = new URL(anchor.href, window.location.origin);
      return url.origin === window.location.origin && url.pathname.replace(/\/+$/u, "") === "/pages/contact";
    } catch {
      return false;
    }
  }

  function recommendationPayload(element) {
    const sourceProductHandle = safeProductHandle(element?.dataset?.sourceProductHandle);
    const recommendedProductHandle = safeProductHandle(element?.dataset?.recommendedProductHandle);
    const placement = element?.dataset?.placement;

    if (!sourceProductHandle || !recommendedProductHandle || !["page", "drawer"].includes(placement)) return null;
    return {
      source: "cart_recommendation",
      placement,
      source_product_handle: sourceProductHandle,
      recommended_product_handle: recommendedProductHandle,
    };
  }

  function recommendationKey(payload) {
    return `${payload.placement}:${payload.source_product_handle}:${payload.recommended_product_handle}`;
  }

  function productClickPlacement(anchor, pageContext) {
    if (pageContext.kind === "guide") {
      // The dedicated guide excludes shared header/footer links from its content clicks.
      if (pageContext.handle === "lifestyle-care-guide" && !anchor.closest("[data-lifestyle-guide]")) return "";
      return "guide_body";
    }
    if (pageContext.kind === "care_support") return "care_support_card";
    if (pageContext.kind !== "care_note") return "";
    if (anchor.closest("[data-mk-care-steps]")) return "care_steps";
    if (anchor.closest("[data-mk-article-related-products]")) return "related_products";
    return "article_body";
  }

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    const printButton = event.target.closest("[data-mk-care-support-print]");
    if (printButton) {
      window.print();
      return;
    }

    if (!analyticsAllowed()) return;

    const anchor = event.target.closest("a[href]");
    if (!anchor) return;

    const recommendationLink = anchor.closest("[data-mk-cart-recommendation-link]");
    if (recommendationLink) {
      const payload = recommendationPayload(recommendationLink.closest("[data-mk-cart-recommendation]"));
      if (payload) publishEvent("masterkey:cart_recommendation_clicked", payload);
      return;
    }

    const pageContext = currentPageContext();

    if (pageContext.kind === "faq" && isContactLink(anchor)) {
      publishEvent("masterkey:faq_contact_clicked", {
        source: "faq",
        placement: "faq_page",
      });
      return;
    }

    const productHandle = productHandleFromLink(anchor);
    const placement = productClickPlacement(anchor, pageContext);
    if (!productHandle || !placement) return;

    publishEvent("masterkey:content_product_clicked", {
      source: pageContext.kind,
      placement,
      content_handle: pageContext.handle,
      product_handle: productHandle,
    });
  }, true);

  document.addEventListener("toggle", (event) => {
    if (!analyticsAllowed() || currentPageContext().kind !== "faq") return;
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!details.closest(".shopify-section--faq") || openedFaqItems.has(details)) return;

    const faqItems = Array.from(document.querySelectorAll(".shopify-section--faq details"));
    const itemIndex = faqItems.indexOf(details);
    const explicitId = details.dataset?.mkFaqId || details.id;
    const faqId = safeProductHandle(explicitId) || `question-${Math.max(itemIndex, 0) + 1}`;

    if (publishEvent("masterkey:faq_opened", {
      source: "faq",
      faq_id: faqId,
    })) {
      openedFaqItems.add(details);
    }
  }, true);

  function usageSectionPayload(element) {
    const pageContext = currentPageContext();
    if (pageContext.kind === "product") {
      return {
        context: "product",
        product_handle: pageContext.handle,
        section_kind: element.matches("[data-mk-product-usage]") ? "product_usage" : "how_to_care",
      };
    }
    if (pageContext.kind === "care_note") {
      return {
        context: "care_note",
        content_handle: pageContext.handle,
        section_kind: "care_steps",
      };
    }
    return null;
  }

  function clearUsageTimer(element) {
    const timerId = pendingUsageTimers.get(element);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      pendingUsageTimers.delete(element);
    }
  }

  function startUsageObserver() {
    if (usageObserverStarted || !analyticsAllowed() || typeof window.IntersectionObserver !== "function") return;
    usageObserverStarted = true;

    const usageSections = Array.from(document.querySelectorAll("[data-mk-product-usage], #how-to-care, [data-mk-care-steps]"));
    if (usageSections.length === 0) return;

    usageObserver = new window.IntersectionObserver((entries) => {
      for (const entry of entries) {
        const element = entry.target;
        if (viewedUsageSections.has(element)) {
          usageObserver?.unobserve(element);
          clearUsageTimer(element);
          continue;
        }

        if (!entry.isIntersecting || entry.intersectionRatio < usageVisibilityRatio) {
          clearUsageTimer(element);
          continue;
        }

        if (pendingUsageTimers.has(element)) continue;
        const timerId = window.setTimeout(() => {
          pendingUsageTimers.delete(element);
          if (!analyticsAllowed()) return;
          const payload = usageSectionPayload(element);
          if (!payload) return;

          if (publishEvent("masterkey:usage_section_viewed", payload)) {
            viewedUsageSections.add(element);
            usageObserver?.unobserve(element);
          }
        }, usageVisibilityDelayMs);
        pendingUsageTimers.set(element, timerId);
      }
    }, { threshold: [0, usageVisibilityRatio] });

    usageSections.forEach((element) => usageObserver.observe(element));
  }

  function clearRecommendationTimer(element) {
    const timerId = pendingRecommendationTimers.get(element);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      pendingRecommendationTimers.delete(element);
    }
  }

  function observeCartRecommendations() {
    if (!analyticsAllowed() || typeof window.IntersectionObserver !== "function") return;
    const recommendations = Array.from(document.querySelectorAll("[data-mk-cart-recommendation]"));
    if (recommendations.length === 0) return;

    if (!recommendationObserver) {
      recommendationObserver = new window.IntersectionObserver((entries) => {
        for (const entry of entries) {
          const element = entry.target;
          const payload = recommendationPayload(element);
          if (!payload) {
            recommendationObserver?.unobserve(element);
            clearRecommendationTimer(element);
            continue;
          }

          const key = recommendationKey(payload);
          if (viewedRecommendationKeys.has(key)) {
            recommendationObserver?.unobserve(element);
            clearRecommendationTimer(element);
            continue;
          }

          if (!entry.isIntersecting || entry.intersectionRatio < recommendationVisibilityRatio) {
            clearRecommendationTimer(element);
            continue;
          }

          if (pendingRecommendationTimers.has(element)) continue;
          const timerId = window.setTimeout(() => {
            pendingRecommendationTimers.delete(element);
            if (!analyticsAllowed()) return;
            const currentPayload = recommendationPayload(element);
            if (!currentPayload) return;
            const currentKey = recommendationKey(currentPayload);

            if (viewedRecommendationKeys.has(currentKey)) {
              recommendationObserver?.unobserve(element);
              return;
            }

            if (publishEvent("masterkey:cart_recommendation_viewed", currentPayload)) {
              viewedRecommendationKeys.add(currentKey);
              recommendationObserver?.unobserve(element);
            }
          }, recommendationVisibilityDelayMs);
          pendingRecommendationTimers.set(element, timerId);
        }
      }, { threshold: [0, recommendationVisibilityRatio] });
    }

    for (const element of recommendations) {
      const payload = recommendationPayload(element);
      if (!payload || viewedRecommendationKeys.has(recommendationKey(payload)) || observedRecommendationElements.has(element)) continue;
      observedRecommendationElements.add(element);
      recommendationObserver.observe(element);
    }
  }

  function consumeSuccessQuery(parameterName) {
    try {
      if (typeof window.history?.replaceState !== "function") return;

      const currentUrl = new URL(window.location.href);
      if (!currentUrl.searchParams.getAll(parameterName).includes("true")) return;

      currentUrl.searchParams.delete(parameterName);
      window.history.replaceState(
        window.history.state,
        "",
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
      );
    } catch {
      // URL cleanup is best-effort and must never affect the success UI or tracking flow.
    }
  }

  function publishContactSuccess() {
    if (contactSuccessPublished || !analyticsAllowed() || currentPageContext().kind !== "contact") return;
    if (!document.querySelector("[data-mk-contact-success]")) return;

    if (publishEvent("masterkey:contact_submitted", {
      source: "contact_page",
    })) {
      contactSuccessPublished = true;
      consumeSuccessQuery("contact_posted");
    }
  }

  function publishNewsletterSuccess() {
    if (newsletterSuccessPublished || !analyticsAllowed()) return;
    if (!document.querySelector("[data-mk-newsletter-success]")) return;

    if (publishEvent("masterkey:newsletter_subscribed", {
      source: "newsletter_form",
    })) {
      newsletterSuccessPublished = true;
      consumeSuccessQuery("customer_posted");
    }
  }

  function startMeasurement() {
    if (!analyticsAllowed()) return;
    startUsageObserver();
    observeCartRecommendations();
    publishContactSuccess();
    publishNewsletterSuccess();
  }

  function stopMeasurement() {
    usageObserver?.disconnect();
    usageObserver = null;
    usageObserverStarted = false;
    recommendationObserver?.disconnect();
    recommendationObserver = null;
    observedRecommendationElements = new WeakSet();
    pendingUsageTimers.forEach((timerId) => window.clearTimeout(timerId));
    pendingUsageTimers.clear();
    pendingRecommendationTimers.forEach((timerId) => window.clearTimeout(timerId));
    pendingRecommendationTimers.clear();
  }

  document.addEventListener("cart-drawer:refreshed", () => {
    observeCartRecommendations();
  });

  document.addEventListener("visitorConsentCollected", () => {
    if (analyticsAllowed()) startMeasurement();
    else stopMeasurement();
  });

  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === "function") {
    startMeasurement();
  } else if (typeof window.Shopify?.loadFeatures === "function") {
    try {
      window.Shopify.loadFeatures([{
        name: "consent-tracking-api",
        version: "0.1",
      }], (error) => {
        if (!error) startMeasurement();
      });
    } catch {
      // Fail closed until Shopify exposes an affirmative analytics decision.
    }
  }
})();
